import classNames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { MapModes, operationModesList } from "constant";
import features from "features";
import { calculateArea } from "helpers/calculateArea";
import { customToFixed } from "helpers/numbers";
import { RootStateInterface } from "reducer";
import { MapStateInterface } from "scenes/MapPage/interfaces";
import { MissionsManagerStateInterface } from "scenes/MissionsManager/interfaces";
import MapRouteInfoAccordion from "../MapRouteInfoAccordion/MapRouteInfoAccordion";
import MapUGVDetails from "../MapUGVDetails/MapUGVDetails";

import Accordion from "components/Accordion/Accordion";
import CommonButton from "components/buttons/CommonButton/CommonButton";
import DropdownMenu from "components/DropdownMenu/DropdownMenu";
import ExpandBlock from "components/ExpandBlock/ExpandBlock";
import Switcher from "components/Switcher/Switcher";

import ChevronDownIcon from "assets/icons/chevron-down.svg";
import DownloadIcon from "assets/icons/download.svg";
import ListIcon from "assets/icons/list.svg";
import PlayIcon from "assets/icons/play.svg";
import PlusBlackIcon from "assets/icons/plus-black.svg";
import RoutTripIcon from "assets/icons/rout-trip.svg";
import TrashSilverIcon from "assets/icons/trash-silver.svg";
import TrashIcon from "assets/icons/trash.svg";
import UGVIcon from "assets/icons/ugv.svg";

import styles from "./map-routes.module.scss";

const MapRoutes = () => {
  const dispatch = useDispatch();

  const { id: projectId } = useParams();

  const { missions, mission, isRunRouteAnalysisLoading } = useSelector<
    RootStateInterface,
    MissionsManagerStateInterface
  >((state) => state.missionsManager);
  const { operationMode } = useSelector<RootStateInterface, MapStateInterface>(
    (state) => state.map
  );

  const toggleRef = useRef(null);
  const isOpenRef = useRef(null);

  const [contextTop, setContextTop] = useState(undefined);
  const [isContext, setIsContext] = useState(false);
  const [ugvDetailsPosition, setUGVDetailsPosition] = useState(undefined);

  const onChangeCurrentMode = useCallback(
    (val) => () => {
      dispatch(features.map.actions.setOperationMode(val));
    },
    [dispatch]
  );

  const onRouteClick = useCallback((e) => {
    const positionData = e.target.getBoundingClientRect();
    setContextTop(positionData.top - positionData.height);
    setIsContext(true);
  }, []);
  const hideContextMenu = useCallback(() => {
    setIsContext(false);
    setTimeout(() => {
      setContextTop(undefined);
    }, 400);
  }, []);
  const onRouteOptionClick = useCallback(
    (option) => {
      if (option) {
        dispatch(
          features.missionsManager.actions.fetchMissionRequest({
            params: { projectId, missionId: option.value }
          })
        );
      }
      hideContextMenu();
    },
    [dispatch, hideContextMenu, projectId]
  );

  const onUGVClick = useCallback(
    (index) => (e) => {
      const positionData = e.currentTarget.getBoundingClientRect();

      setUGVDetailsPosition({
        top: positionData.top,
        left: positionData.left - 380
      });

      dispatch(features.missionsManager.actions.setVehicle(index));

      if (!isOpenRef.current) {
        toggleRef.current();
      }
    },
    [dispatch]
  );
  const hideUGVDetails = useCallback(() => {
    setUGVDetailsPosition(undefined);
    dispatch(features.missionsManager.actions.clearVehicle());
  }, [dispatch]);

  const onRunRouteAnalysis = useCallback(() => {
    const { id: missionId } = mission;
    dispatch(
      features.missionsManager.actions.runRouteAnalysisRequest({
        params: { projectId, missionId }
      })
    );
  }, [dispatch, mission, projectId]);

  const missionsOptions = useMemo(() => {
    if (missions?.length)
      return missions.map((mission) => {
        return { label: mission.name, value: mission.id };
      });
    else return [];
  }, [missions]);

  // if (routeEdit) return <MapRoute route={routeEdit} />;

  useEffect(() => {
    dispatch(
      features.missionsManager.actions.fetchMissionsRequest({
        params: { projectId }
      })
    );
    // dispatch(features.missionsManager.actions.fetchVehiclesRequest());
  }, [dispatch, projectId]);

  const areaInSquareMeters = useMemo(() => {
    if (mission?.data?.polygon?.coordinates) {
      const coordinates = mission?.data?.polygon.coordinates[0];
      const calculated = calculateArea(coordinates).areaInSquareMeters;
      return customToFixed(calculated, 2);
    }
  }, [mission]);

  const onDeleteVehicle = useCallback(
    (vehicleIndex) => (e) => {
      e.stopPropagation();
      dispatch(
        features.missionsManager.actions.deleteVehicleRequest({
          params: {
            projectId,
            vehicleIndex
          }
        })
      );
    },
    [dispatch, projectId]
  );

  return (
    <Accordion
      className={styles["map-routes-accordion"]}
      isOpenDefault
      isScroll
      header={({ isOpen, onToggleAccordion }) => {
        toggleRef.current = onToggleAccordion;
        isOpenRef.current = isOpen;
        return (
          <div
            className={classNames(styles["accordion-head"], {
              [styles["accordion-head--active"]]: isOpen
            })}
            onClick={onToggleAccordion}
          >
            <span className={styles["accordion-head__label"]}>Routes</span>
            <div className={styles["accordion-head__icon"]}>
              <img
                className={styles["icon"]}
                src={ListIcon}
                alt="layers-button"
              />
            </div>
          </div>
        );
      }}
      overflowedChildren={
        <>
          <div style={{ position: "absolute", top: contextTop, left: "16px" }}>
            <DropdownMenu
              options={missionsOptions}
              isActive={isContext}
              onClick={onRouteOptionClick}
            />
          </div>
          {ugvDetailsPosition && (
            <MapUGVDetails
              onHide={hideUGVDetails}
              style={{
                position: "absolute",
                top: ugvDetailsPosition.top,
                left: ugvDetailsPosition.left,
                zIndex: 9999999
              }}
            />
          )}
        </>
      }
    >
      <div className={styles["accordion-body"]}>
        <div className={styles["accordion-container"]}>
          <Switcher
            options={operationModesList}
            currentOption={operationMode}
            onChange={onChangeCurrentMode}
            className={styles["routes-mode-switcher"]}
          />
        </div>
        <ExpandBlock title={"Area"} isOpenDefault>
          <div className={styles["accordion-container"]}>
            <CommonButton
              onClick={() => {
                dispatch(
                  features.map.actions.setMapMode(
                    MapModes.MISSION_POLYGON_CREATION
                  )
                );
              }}
              icon={RoutTripIcon}
            >
              Create Route
            </CommonButton>
            {missions?.length > 0 && (
              <div className={styles["routes-actions"]}>
                <button
                  className={classNames(styles["route-select-btn"], {
                    [styles["route-select-btn--active"]]: isContext
                  })}
                  key={mission?.name}
                  onClick={(e) =>
                    setTimeout(() => {
                      onRouteClick(e);
                    }, 0)
                  }
                >
                  {mission?.name}
                  <div className={styles["route-select-btn__icon"]}>
                    <img src={ChevronDownIcon} alt="chevron-down-icon" />
                  </div>
                </button>
                <div
                  className={styles["route-delete"]}
                  onClick={() => {
                    dispatch(
                      features.missionsManager.actions.deleteMissionRequest({
                        params: { projectId, missionId: mission.id }
                      })
                    );
                  }}
                >
                  <img src={TrashIcon} alt="trash-icon"></img>
                </div>
                <div className={styles["route-square"]}>
                  Square: <span>{areaInSquareMeters} m2</span>
                </div>
              </div>
            )}
          </div>
        </ExpandBlock>
        <ExpandBlock title={"UGVs"} isOpenDefault>
          <div
            className={classNames(
              styles["accordion-container"],
              styles["UGVs-container"]
            )}
          >
            {mission?.data?.vehicles.map((vehicle, i) => (
              <div
                onClick={onUGVClick(i)}
                className={styles["UGV-item"]}
                key={`vehicle-index-${i}`}
                id={`vehicle-${i}`}
              >
                <div className={styles["UGV-item__icon"]}>
                  <img src={UGVIcon} alt="ugv-icon" />
                </div>
                <div className={styles["UGV-item__title"]}>
                  Vehicle # {i + 1}
                </div>
                {/* <div className={styles["UGV-item__speed"]}>5 km/h</div> */}
                <div className={styles["UGV-item__width"]}>
                  {vehicle.head_width} mm
                </div>
                <div
                  onClick={onDeleteVehicle(i)}
                  className={styles["UGV-item__delete"]}
                >
                  <img src={TrashSilverIcon} alt="trash-icon" />
                </div>
              </div>
            ))}
            <CommonButton
              onClick={onUGVClick(undefined)}
              icon={PlusBlackIcon}
              isSmall
              disabled={missions.length === 0}
            >
              Add UGV
            </CommonButton>
            <div className={styles["route-actions"]}>
              <CommonButton icon={DownloadIcon} disabled>
                Add lidar data
              </CommonButton>
              <CommonButton
                onClick={onRunRouteAnalysis}
                icon={PlayIcon}
                disabled={mission?.data?.vehicles.length === 0}
                isLoading={isRunRouteAnalysisLoading}
              >
                Route analysis
              </CommonButton>
            </div>
            <MapRouteInfoAccordion />
          </div>
        </ExpandBlock>
      </div>
    </Accordion>
  );
};
export default MapRoutes;
